import { Palette, Check, RotateCcw, Type } from "lucide-react";
import { toast } from "sonner";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { THEMES } from "@/lib/theme";

export default function ThemeSelectorCard() {
  const { config, userTheme, setUserTheme, clearUserTheme } = useSiteConfig();
  const globalTheme = config?.theme || "arctic";
  const activeTheme = userTheme || globalTheme;

  const choose = (id) => {
    setUserTheme(id);
    toast.success(`Tema cambiado · ${THEMES.find((t) => t.id === id)?.name}`);
  };

  const reset = () => {
    clearUserTheme();
    toast.success("Volviendo al tema por defecto del sitio");
  };

  return (
    <section data-testid="theme-selector-card" className="border hairline bg-cream-card rounded-2xl p-6 md:p-8 mt-8">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral inline-flex items-center gap-1.5">
            <Palette className="w-3 h-3" /> Apariencia
          </p>
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-1">Tema visual</h2>
          <p className="text-sm text-ink-mid mt-1">
            Elige cómo se ve <strong>y cómo se lee</strong> Ovamx para ti. Cada tema trae su
            propia paleta y tipografías. Se guarda solo en este dispositivo.
          </p>
        </div>
        {userTheme && (
          <button
            onClick={reset}
            data-testid="theme-reset-btn"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border hairline hover:border-coral transition"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Usar tema del sitio
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {THEMES.map((t) => {
          const isActive = activeTheme === t.id;
          const isUserChoice = userTheme === t.id;
          return (
            <button
              key={t.id}
              onClick={() => choose(t.id)}
              data-testid={`theme-option-${t.id}`}
              className={`relative text-left p-4 rounded-2xl border-2 transition-all overflow-hidden
                ${isActive ? "border-coral shadow-md" : "border-transparent hairline border hover:border-coral"}`}
              style={{ background: t.bg, color: t.ink }}
            >
              {isActive && (
                <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-coral text-white z-10">
                  <Check className="w-3.5 h-3.5" strokeWidth={3} />
                </span>
              )}
              <div className="flex items-center gap-2 mb-3">
                <span className="w-5 h-5 rounded-full border" style={{ background: t.accent, borderColor: t.ink + "20" }} />
                <span className="w-3 h-3 rounded-full" style={{ background: t.ink }} />
                <span
                  className="w-3 h-3 rounded-full"
                  style={{
                    background: t.bg,
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderColor: t.ink + "30",
                  }}
                />
                {t.dark && (
                  <span className="ml-auto text-[9px] font-mono uppercase tracking-wider opacity-70">
                    Nocturno
                  </span>
                )}
              </div>
              <p className="text-xl tracking-tight" style={{ color: t.ink, fontWeight: 500 }}>{t.name}</p>
              <p className="font-mono text-[10px] uppercase tracking-wider mt-1 opacity-70">{t.sub}</p>
              <p className="inline-flex items-center gap-1 mt-2 text-[10px] opacity-70">
                <Type className="w-3 h-3" /> {t.fontLabel}
              </p>
              {isUserChoice && (
                <p className="font-mono text-[9px] uppercase tracking-wider mt-3 text-coral">◊ Tu elección</p>
              )}
              {!userTheme && t.id === globalTheme && (
                <p className="font-mono text-[9px] uppercase tracking-wider mt-3 opacity-60">◊ Tema del sitio</p>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}
